import { useState } from 'react'
import { ContentMetadataText } from '@quebon/shared-learning/components/common/ContentMetadataText'
import type { Ref } from 'react'
import { useTranslation } from 'react-i18next'
import { Check, LockKeyhole, Play } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { Lesson, SubjectColor } from '@/pages/learner/ui-model/types'

export interface LessonCardProps {
  lesson: Lesson
  onClick?: (lesson: Lesson) => void
  className?: string
  elementRef?: Ref<HTMLButtonElement>
}

const gradientByColor: Record<SubjectColor, string> = {
  orange: 'linear-gradient(135deg, var(--color-lc-orange-900), var(--color-lc-orange-600))',
  blue: 'linear-gradient(135deg, var(--color-lc-lightblue-900), var(--color-lc-lightblue-600))',
  mint: 'linear-gradient(135deg, var(--color-lc-mint-900), var(--color-lc-mint-300))',
  purple: 'linear-gradient(135deg, var(--color-lc-purple-900), var(--color-lc-purple-300))',
  pink: 'linear-gradient(135deg, var(--color-lc-pink-900), var(--color-lc-pink-200))',
}

export function LessonCard({ lesson, onClick, className, elementRef }: LessonCardProps) {
  const { t } = useTranslation('learner')
  const clickable = lesson.status !== 'locked' && onClick != null
  const statusText = lesson.status === 'done'
    ? t('lessonCard.status.done')
    : lesson.status === 'current'
      ? t('lessonCard.status.current')
      : t('lessonCard.status.locked')
  const lessonNumber = String(lesson.order).padStart(2, '0')

  return (
    <button
      ref={elementRef}
      type="button"
      onClick={clickable ? () => onClick(lesson) : undefined}
      disabled={!clickable}
      aria-label={`${lessonNumber}. ${lesson.title} ${statusText}`}
      className={cn(
        'flex h-[84px] w-full items-center gap-2 rounded-2xl border py-2.5 pl-2.5 pr-5 text-left transition-transform focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-lc-key-500',
        lesson.status === 'done' && 'border-lc-v3-gray-300 bg-lc-bg-primary shadow-lc-v3-card',
        lesson.status === 'current' && 'border-2 border-lc-key-500 bg-lc-bg-key-light shadow-lc-v3-active',
        lesson.status === 'locked' && 'cursor-not-allowed border-lc-disabled-border bg-lc-disabled-bg',
        clickable && 'hover:-translate-y-0.5',
        className,
      )}
    >
      <div
        className="relative h-16 w-[114px] shrink-0 overflow-hidden rounded-[10px] border border-black/10"
        style={{
          background: lesson.status === 'locked'
            ? 'var(--color-lc-v3-gray-300)'
            : gradientByColor[lesson.thumbColor],
        }}
      >
        {lesson.thumbnailUrl ? (
          <LessonThumbnail
            key={lesson.thumbnailUrl}
            url={lesson.thumbnailUrl}
            locked={lesson.status === 'locked'}
          />
        ) : null}
      </div>

      <span className="flex min-w-0 flex-1 flex-col items-start justify-center gap-1">
        <span
          className={cn(
            'text-lc-v3-body-2 font-semibold',
            lesson.status === 'current' ? 'text-lc-text-link' :
              lesson.status === 'locked' ? 'text-lc-text-disabled' : 'text-lc-text-tertiary',
          )}
        >
          {t('lessonCard.number', { order: lessonNumber })}
        </span>
        <strong
          className={cn(
            'line-clamp-1 text-lc-v3-body-1 font-medium',
            lesson.status === 'current' && 'font-semibold text-lc-text-primary',
            lesson.status === 'done' && 'text-lc-text-primary',
            lesson.status === 'locked' && 'text-lc-text-tertiary',
          )}
        >
          <ContentMetadataText text={lesson.title} />
        </strong>
      </span>

      <span
        aria-hidden
        className={cn(
          'flex size-8 shrink-0 items-center justify-center rounded-[10px] text-lc-text-inverse',
          lesson.status === 'done' && 'bg-lc-success-500',
          lesson.status === 'current' && 'bg-lc-key-500',
          lesson.status === 'locked' && 'bg-lc-v3-gray-300 text-lc-v3-gray-500',
        )}
      >
        {lesson.status === 'done' ? (
          <Check className="size-[18px] stroke-[3]" />
        ) : lesson.status === 'current' ? (
          <Play className="size-[18px] translate-x-px fill-current" />
        ) : (
          <LockKeyhole className="size-[18px]" />
        )}
      </span>
    </button>
  )
}

function LessonThumbnail({ url, locked }: { url: string; locked: boolean }) {
  const [failed, setFailed] = useState(false)

  if (failed) return null

  return (
    <img
      src={url}
      alt=""
      loading="lazy"
      decoding="async"
      onError={() => setFailed(true)}
      className={cn(
        'absolute inset-0 size-full object-cover',
        locked && 'grayscale opacity-50',
      )}
    />
  )
}
